import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useDebounce } from '@/hooks'
import { useAuth } from '@/lib'
import { ConfirmationDialog, type ConfirmationDialogRef, SearchFilter } from '@/ui/rapid-ui'
import {
	type AtsJobDetail,
	type JobData,
	komboGetJobs,
	komboGetJobsQueryKey,
	useExportIntegrationIndex,
} from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { CheckIcon, Loader2Icon, MapPinIcon, TriangleAlertIcon } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type AtsJobSelection = {
	exportIntegrationId: string
	atsJobId: string
	name: string
}

type StatusFilter = 'OPEN' | 'ALL'

type AtsJobPickerProps = {
	organisationId: string
	value: AtsJobSelection | null
	onChange: (selection: AtsJobSelection | null) => void
	errors?: string[]
}

const PAGE_SIZE = 20

export function AtsJobPicker({ organisationId, value, onChange, errors = [] }: AtsJobPickerProps) {
	const { t } = useTranslation('common')
	const integrations = useExportIntegrationIndex(
		{ 'filter[organisation_id]': organisationId },
		{ enabled: !!organisationId, retry: false },
	)
	const integrationId = integrations.data?.[0]?.id

	if (integrations.isPending) {
		return <PickerMessage>{t('editor.details.atsJobs.loading')}</PickerMessage>
	}

	if (integrations.isError) {
		return <PickerMessage>{t('editor.details.atsJobs.loadFailed')}</PickerMessage>
	}

	if (!integrationId) {
		return <NotConnected />
	}

	return <JobList integrationId={integrationId} value={value} onChange={onChange} errors={errors} />
}

function NotConnected() {
	const { t } = useTranslation('common')
	const { can } = useAuth()
	const { companyId } = useParams({ strict: false })

	return (
		<PickerMessage>
			<p className="font-medium text-grey-700">{t('editor.details.atsJobs.notConnected')}</p>
			{can('organisation:update') && companyId
				? (
					<Link
						to="/company/$companyId/settings/export"
						params={{ companyId }}
						className="text-brand-700 hover:text-brand-600 underline font-medium"
					>
						{t('editor.details.atsJobs.connect')}
					</Link>
				)
				: <p>{t('editor.details.atsJobs.askAdminToConnect')}</p>}
		</PickerMessage>
	)
}

type JobListProps = {
	integrationId: string
	value: AtsJobSelection | null
	onChange: (selection: AtsJobSelection | null) => void
	errors: string[]
}

function JobList({ integrationId, value, onChange, errors }: JobListProps) {
	const { t } = useTranslation('common')
	const confirmationRef = useRef<ConfirmationDialogRef>(null)
	const [search, setSearch] = useState('')
	const [status, setStatus] = useState<StatusFilter>('OPEN')
	const debouncedSearch = useDebounce(search, 400)

	const params = {
		statuses: status === 'OPEN' ? 'OPEN' : undefined,
		nameContains: debouncedSearch || undefined,
		pageSize: PAGE_SIZE,
	}

	const jobs = useInfiniteQuery({
		queryKey: [...komboGetJobsQueryKey(integrationId, params), 'infinite'],
		queryFn: ({ pageParam, signal }) =>
			komboGetJobs(integrationId, { ...params, cursor: pageParam ?? undefined }, { signal }).then((response) =>
				response.data
			),
		initialPageParam: null as string | null,
		getNextPageParam: (lastPage) => lastPage.data.next ?? null,
	})

	const results = jobs.data?.pages.flatMap((page) => page.data.results) ?? []

	const select = async (job: JobData) => {
		if (job.status !== 'OPEN') {
			try {
				await confirmationRef.current?.confirm({
					title: t('editor.details.atsJobs.notOpenTitle'),
					message: t('editor.details.atsJobs.notOpenMessage', { name: job.name ?? '' }),
					confirmText: t('editor.details.atsJobs.notOpenConfirm'),
					cancelText: t('common.cancel'),
				})
			} catch {
				// cancelled
				return
			}
		}

		onChange({ exportIntegrationId: integrationId, atsJobId: job.id, name: job.name ?? '' })
	}

	return (
		<div className="flex flex-col gap-3 p-4">
			<div className="flex flex-row gap-2">
				<Select value={status} onValueChange={(value) => setStatus(value as StatusFilter)}>
					<SelectTrigger aria-label={t('editor.details.atsJobs.statusFilter')} className="bg-white">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="OPEN">{t('editor.details.atsJobs.openJobs')}</SelectItem>
						<SelectItem value="ALL">{t('editor.details.atsJobs.allJobs')}</SelectItem>
					</SelectContent>
				</Select>
				<div className="flex-1 min-w-0">
					<SearchFilter
						value={search}
						onChange={setSearch}
						placeholder={t('editor.details.atsJobs.search')}
					/>
				</div>
			</div>

			<div
				role="listbox"
				aria-label={t('editor.details.atsJobs.jobs')}
				className={ctl(`
					flex flex-col gap-2 max-h-96 overflow-y-auto pr-1
					${jobs.isFetching && !jobs.isFetchingNextPage && 'opacity-50'}
				`)}
			>
				{jobs.isPending
					? <PickerMessage>{t('editor.details.atsJobs.loading')}</PickerMessage>
					: jobs.isError
					? <PickerMessage>{t('editor.details.atsJobs.loadFailed')}</PickerMessage>
					: results.length === 0
					? <PickerMessage>{t('editor.details.atsJobs.noJobs')}</PickerMessage>
					: results.map((job) => (
						<JobOption
							key={job.id}
							job={job}
							selected={value?.atsJobId === job.id && value.exportIntegrationId === integrationId}
							onSelect={() => void select(job)}
						/>
					))}

				{jobs.hasNextPage
					? (
						<button
							type="button"
							onClick={() => void jobs.fetchNextPage()}
							disabled={jobs.isFetchingNextPage}
							className="self-center text-sm font-medium text-brand-700 hover:text-brand-600 py-2"
						>
							{jobs.isFetchingNextPage
								? <Loader2Icon className="size-4 animate-spin" />
								: t('editor.details.atsJobs.loadMore')}
						</button>
					)
					: null}
			</div>

			{errors.length > 0 && <p className="text-xs text-red-500">{errors.join(', ')}</p>}

			<ConfirmationDialog ref={confirmationRef} />
		</div>
	)
}

type JobOptionProps = {
	job: JobData
	selected: boolean
	onSelect: () => void
}

function JobOption({ job, selected, onSelect }: JobOptionProps) {
	const { t } = useTranslation('common')
	const { companyId } = useParams({ strict: false })
	const location = job.location?.raw || [job.location?.city, job.location?.country].filter(Boolean).join(', ')
	const blockedReason = job.openCampaignId
		? t('editor.details.atsJobs.hasOpenCampaign')
		: job.missingDetails.length > 0
		? missingDetailsReason(job.missingDetails, t)
		: null

	return (
		<div
			role="option"
			aria-selected={selected}
			aria-disabled={blockedReason !== null}
			className={ctl(`
				rounded-lg border p-3 text-left transition-colors
				${selected ? 'border-brand-500 bg-brand-50' : 'border-grey-200 bg-white'}
				${blockedReason === null && !selected && 'hover:border-brand-300 hover:bg-brand-50/50 cursor-pointer'}
				${blockedReason !== null && 'opacity-75'}
			`)}
			onClick={blockedReason === null ? onSelect : undefined}
			onKeyDown={(event) => {
				if (blockedReason === null && (event.key === 'Enter' || event.key === ' ')) {
					event.preventDefault()
					onSelect()
				}
			}}
			tabIndex={blockedReason === null ? 0 : -1}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="flex-1 min-w-0">
					<div className="text-sm font-medium text-grey-900 truncate">
						{job.name || t('editor.details.atsJobs.untitled')}
					</div>
					<div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-grey-500">
						{job.status
							? (
								<span
									className={ctl(`
									rounded px-1.5 py-0.5 font-medium
									${
										job.status === 'OPEN'
											? 'bg-green-100 text-green-700'
											: 'bg-grey-100 text-grey-600'
									}
								`)}
								>
									{job.status}
								</span>
							)
							: null}
						{location
							? (
								<span className="flex items-center gap-1">
									<MapPinIcon className="size-3" />
									{location}
								</span>
							)
							: null}
						{job.hasCampaign && !job.openCampaignId
							? <span>{t('editor.details.atsJobs.hasCampaign')}</span>
							: null}
					</div>
				</div>
				{selected ? <CheckIcon className="size-5 shrink-0 text-brand-600" /> : null}
			</div>

			{blockedReason
				? (
					<div className="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
						<TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0 text-amber-500" />
						<span>
							{blockedReason} {job.openCampaignId && companyId
								? (
									<Link
										to="/company/$companyId/campaign/$campaignId"
										params={{ companyId, campaignId: job.openCampaignId }}
										className="font-medium underline"
									>
										{t('editor.details.atsJobs.openCampaign')}
									</Link>
								)
								: t('editor.details.atsJobs.fixInAts')}
						</span>
					</div>
				)
				: null}
		</div>
	)
}

function missingDetailsReason(missingDetails: AtsJobDetail[], t: (key: string) => string): string {
	return missingDetails
		.map((detail) => t(`editor.details.atsJobs.missing.${detail}`))
		.join(' ')
}

function PickerMessage({ children }: { children: React.ReactNode }) {
	return <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-grey-500">{children}</div>
}
